import React, { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import { CustomHtmlSection, CustomHtmlStyles } from 'components/commons/CustomHtml';
import { isSlideImage, useRenderableHeroSliders } from 'helpers/sliders';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './HomeMan.scss';

/* ---------- RowCarousel reutilizable ---------- */
const RowCarousel = ({ items = [], renderItem, ariaLabel, itemMinWidth = 220 }) => {
  const trackRef = useRef(null);
  const scrollByDir = (dir) => {
    const node = trackRef.current;
    if (!node) return;
    const amount = Math.max(node.clientWidth * 0.9, itemMinWidth * 1.5);
    node.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };
  if (!items?.length) return null;
  return (
    <div className="hm-row" role="region" aria-label={ariaLabel}>
      <button type="button" className="hm-row__nav hm-row__nav--prev" aria-label="Anterior" onClick={() => scrollByDir(-1)}>‹</button>
      <div className="hm-row__track" ref={trackRef} style={{ '--hm-item-min': `${itemMinWidth}px` }}>
        {items.map((it, idx) => (
          <div className="hm-row__item" key={it?.id ?? idx}>
            {renderItem(it)}
          </div>
        ))}
      </div>
      <button type="button" className="hm-row__nav hm-row__nav--next" aria-label="Siguiente" onClick={() => scrollByDir(1)}>›</button>
    </div>
  );
};

/* ---------- helpers ---------- */
const getCatCode = (it) =>
  it?.category?.code ?? it?.category_code ?? it?.categoryCode ?? it?.codeCategory ?? it?.code;
const getSubCode = (it) =>
  it?.subcategory?.code ?? it?.subCategory?.code ?? it?.subcategory_code ?? it?.subCategoryCode;
const getBannerImage = (it) => it?.image ?? it?.imageHome ?? it?.banner ?? it?.url ?? '';
const getBannerImageMobile = (it) => it?.imageMobile ?? getBannerImage(it);
const getCategoryImage = (c) =>
  c?.imageHome ?? c?.image_home ?? c?.image ?? c?.thumbnail ?? c?.banner ?? '';
const getSlideMedia = (slide, onReady, onFailed) => (
  isSlideImage(slide) ? (
    <img
      src={slide.image}
      alt={slide.text || 'Portada'}
      loading="eager"
      decoding="async"
      onLoad={() => onReady(slide)}
      onError={() => onFailed(slide)}
    />
  ) : (
    <video
      src={slide.image}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-label={slide.text || 'Video'}
      onLoadedData={() => onReady(slide)}
      onError={() => onFailed(slide)}
    />
  )
);

/* ---------- Componente principal ---------- */
const HomeMan = ({
  cartel = [],
  sliders = [],
  categories = [],
  categoriesHome = [],
  mostSelled = [],
  featuredProducts = [],
  offers = [],
  marcasHome = [],
  renderItem,
  renderBrand,
  customHtml = null,
  variant = '',
  presentation = null,
}) => {
  // categorías planas
  const flatCategories = useMemo(() => {
    if (categories?.length) return categories;
    if (categoriesHome?.length) return categoriesHome.map((ch) => ch?.category || ch).filter(Boolean);
    return [];
  }, [categories, categoriesHome]);

  // Solo categorías con imagen
  const categoriesWithImage = useMemo(
    () => flatCategories.filter((c) => !!getCategoryImage(c)),
    [flatCategories]
  );

  const goCartel = (i) => {
    const it = cartel?.[i];
    if (!it) return '/productos';
    const cat = getCatCode(it);
    const sub = getSubCode(it);
    if (cat && sub) return `/productos/${cat}/${sub}`;
    if (cat) return `/productos/${cat}`;
    return '/productos';
  };

  const CATGRID_COUNT = 3;
  const heroMode = presentation?.hero || 'carousel';
  const productsMode = presentation?.products || 'carousel';
  const brandsMode = presentation?.brands || 'carousel';
  const productLimit = presentation?.productLimit || 8;
  const {
    heroSliders,
    markHeroSlideReady,
    markHeroSlideFailed,
  } = useRenderableHeroSliders(sliders);

  const renderHeroBlock = () => {
    if (!heroSliders.length) return null;

    if (heroMode === 'carousel' && heroSliders.length === 1) {
      const slide = heroSliders[0];
      return (
        <section className="hm-hero hero-carousel">
          <div className="hm-hero__inner">
            <div className="hm-hero__slide">
              {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
            </div>
          </div>
        </section>
      );
    }

    if (heroMode === 'carousel') {
      return (
      <section className="hm-hero hero-carousel">
        <div className="hm-hero__inner">
          <Carousel
            autoPlay
            infiniteLoop
            showThumbs={false}
            showStatus={false}
            emulateTouch
            swipeable
          >
            {heroSliders.map((slide) => (
              <div key={slide.id || slide.image} className="hm-hero__slide">
                {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
              </div>
            ))}
          </Carousel>
        </div>
      </section>
      );
    }

    const visibleSlides = heroSliders.slice(0, heroMode === 'mosaic' ? 3 : (heroMode === 'split' ? 2 : 1));

    return (
      <section
        className={`hm-hero hero-carousel hm-hero--static hm-hero--${heroMode}`}
      >
        <div className="hm-hero__inner">
          {visibleSlides.map((slide) => (
            <div key={slide.id || slide.image} className="hm-hero__slide">
              {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderCategoriesBlock = () => (
    !!categoriesWithImage.length && (
      <section className="container hm-catgrid">
        {categoriesWithImage.slice(0, CATGRID_COUNT).map((c) => (
          <Link key={c.id ?? c.code ?? c.name} to={`/productos/${c.code}`} className="hm-catgrid__card">
            <div className="hm-catgrid__media">
              <img src={getCategoryImage(c)} alt={c.name} loading="lazy" decoding="async" />
            </div>
            <div className="hm-catgrid__caption"><span>{c.name}</span></div>
          </Link>
        ))}
      </section>
    )
  );

  const renderCategoryLinksBlock = () => (
    !!flatCategories?.length && (
      <section className="hm-catnav container" style={{ marginTop: '21px' }}>
        <div className="hm-catnav__track" role="navigation" aria-label="Categorías">
          {flatCategories.map((c) => (
            <Link key={c.id ?? c.code ?? c.name} to={`/productos/${c.code}`} className="hm-chip">
              {c.name}
            </Link>
          ))}
        </div>
      </section>
    )
  );

  const renderBannerBlock = (index) => (
    cartel[index] ? (
      <section className="hm-banner hm-banner--full">
        <picture>
          <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[index])} />
          <img className="hm-banner__img" src={getBannerImage(cartel[index])} alt="" loading="lazy" decoding="async" />
        </picture>
        <Link className="hm-banner__cta" to={goCartel(index)}>Ver colección</Link>
      </section>
    ) : null
  );

  const renderProductsDisplay = (products, label) => {
    if (productsMode === 'carousel') {
      return <RowCarousel items={products} renderItem={renderItem} ariaLabel={label} itemMinWidth={220} />;
    }

    return (
      <div className={`hm-products hm-products--${productsMode}`} role="region" aria-label={label}>
        {products.slice(0, productLimit).map((product, index) => (
          <div className="hm-products__item" key={product?.id ?? index}>
            {renderItem(product)}
          </div>
        ))}
      </div>
    );
  };

  const renderProductRail = (products, label, link = '/productos') => (
    !!products?.length && (
      <section className="container hm-rail">
        <header className="hm-rail__head">
          <h2>{label}</h2>
          <Link to={link} className="hm-rail__link">Ver todo</Link>
        </header>
        {renderProductsDisplay(products, label)}
      </section>
    )
  );

  const renderMarcasBlock = () => (
    !!marcasHome?.length && (
      <section className="container hm-brands">
        <header className="hm-rail__head">
          <h2>MARCAS</h2>
        </header>
        {brandsMode === 'carousel' ? (
          <RowCarousel
            items={marcasHome}
            ariaLabel="Marcas"
            itemMinWidth={140}
            renderItem={(brand) =>
              renderBrand ? (
                renderBrand(brand)
              ) : (
                <a className="hm-brand" href={brand?.url || '#'} target={brand?.url ? '_blank' : '_self'} rel="noreferrer" aria-label={brand?.name || 'Marca'}>
                  <img src={brand?.image || brand?.logo} alt={brand?.name || ''} />
                </a>
              )
            }
          />
        ) : (
          <div className={`hm-brands__grid hm-brands__grid--${brandsMode}`} role="region" aria-label="Marcas">
            {marcasHome.slice(0, 8).map((brand, index) => {
              const brandName = brand?.name || brand?.category?.name || 'Marca';
              const brandImage = brand?.image || brand?.logo || brand?.category?.imageHome || brand?.category?.image;
              const brandUrl = brand?.url || (brand?.category?.code ? `/productos/${brand.category.code}` : '#');
              const external = /^https?:\/\//.test(brandUrl);
              return renderBrand ? (
                <div className="hm-brands__item" key={brand?.id ?? brand?.category?.id ?? index}>
                  {renderBrand(brand)}
                </div>
              ) : (
                <a
                  className="hm-brand"
                  href={brandUrl}
                  target={external ? '_blank' : '_self'}
                  rel="noreferrer"
                  aria-label={brandName}
                  key={brand?.id ?? brand?.category?.id ?? index}
                >
                  {brandImage && <img src={brandImage} alt={brandName} loading="lazy" decoding="async" />}
                  {brandsMode === 'text' && <span>{brandName}</span>}
                </a>
              );
            })}
          </div>
        )}
      </section>
    )
  );

  const dynamicBlocks = {
    carrusel: renderHeroBlock,
    categorias: renderCategoriesBlock,
    'links-categorias': renderCategoryLinksBlock,
    marcas: renderMarcasBlock,
    'productos-destacados': () => renderProductRail(featuredProducts, 'Novedades'),
    'mas-vendidos': () => renderProductRail(mostSelled, 'Más vendidos'),
    ofertas: () => renderProductRail(offers, 'Ofertas', '/ofertas'),
    'banner-1': () => renderBannerBlock(0),
    'banner-2': () => renderBannerBlock(1),
    'banner-3': () => renderBannerBlock(2),
    'banner-4': () => renderBannerBlock(3),
  };

  if (customHtml?.enabled && customHtml?.mode === 'fullHome' && customHtml?.fullHome) {
    return (
      <div className={`home-man ${variant ? `home-man--${variant}` : ''} home-man--custom-full`.trim()}>
        <CustomHtmlStyles settings={customHtml} />
        <CustomHtmlSection settings={customHtml} section="fullHome" dynamicBlocks={dynamicBlocks} />
      </div>
    );
  }

  return (
    <div className={`home-man ${variant ? `home-man--${variant}` : ''}`.trim()}>
      <CustomHtmlStyles settings={customHtml} />
      <CustomHtmlSection settings={customHtml} section="beforeHero" dynamicBlocks={dynamicBlocks} />
      {/* HERO */}
      {renderHeroBlock()}

      {/* GRID CATEGORÍAS CON FOTO */}
      <CustomHtmlSection settings={customHtml} section="afterHero" dynamicBlocks={dynamicBlocks} />

      {renderCategoriesBlock()}

      {/* NAV CATEGORÍAS (chips) */}
      {renderCategoryLinksBlock()}

      {/* BANNER FULL BLEED */}
      {renderBannerBlock(0)}

      {/* NOVEDADES */}
      <CustomHtmlSection settings={customHtml} section="beforeProducts" dynamicBlocks={dynamicBlocks} />

      {renderProductRail(featuredProducts, 'Novedades')}

      {/* OFERTAS (nuevo) */}
      {renderProductRail(offers, 'Ofertas', '/ofertas')}

      {/* GRID 2-UP DE CAMPAÑA */}
      {(cartel[1] || cartel[2]) && (
        <section className="hm-promo2">
          {cartel.slice(1, 3).map((b, i) => (
            !!b && (
              <Link key={i} to={goCartel(1 + i)} className="hm-promo2__card">
                <picture>
                  <source media="(max-width: 768px)" srcSet={getBannerImageMobile(b)} />
                  <img src={getBannerImage(b)} alt="" loading="lazy" decoding="async" />
                </picture>
              </Link>
            )
          ))}
        </section>
      )}

      {/* MÁS VENDIDOS */}
      {renderProductRail(mostSelled, 'Más vendidos')}

      {/* MARCAS */}
      {renderMarcasBlock()}

      {/* BANNER FINAL */}
      {renderBannerBlock(3)}

    </div>
  );
};

export default HomeMan;
